import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { InputOption } from '../../../../../new-lib/types/search-filters';
import SortIcon from '../../../../../new-components/svg-icons/SortIcon';
import useOnClickOutside from '../../../../../lib/Custom Hooks/ClickOutside';
import { SelectedSort } from './SortByContainer';
import NawyAppBanner from '../../../../../new-components/NawyAppBanner';
import NawyNowSortIcon from '../../../../../new-components/svg-icons/NawyNowSortIcon';

type sortyByFilter = {
  order_by?: string;
  order_direction?: string;
};

interface SortByPresentationpropsTypes {
  options: InputOption[];
  selectedValues: sortyByFilter;
  onChange: (selectedSort: SelectedSort) => void;
  setStackSort?: Dispatch<SetStateAction<boolean>>;
  isNawyNow?: boolean;
}

interface inputPropsType {
  option: InputOption;
}

const SortByPresentation = ({
  options,
  selectedValues,
  onChange,
  setStackSort,
  isNawyNow
}: SortByPresentationpropsTypes): JSX.Element => {
  const { t } = useTranslation('common');

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const closeFilter = () => {
    if (containerRef?.current) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (setStackSort) {
      setStackSort(isOpen);
    }
  }, [isOpen]);

  useOnClickOutside(containerRef, containerRef, () => closeFilter());

  const Input = ({ option }: inputPropsType) => {
    let isSelected = false;
    if (
      selectedValues.order_by &&
      selectedValues.order_direction &&
      selectedValues.order_by + selectedValues.order_direction ===
        option.value + option.options.direction
    ) {
      isSelected = true;
    }

    return (
      <div
        onClick={() => {
          setIsOpen(false);
          onChange(
            isSelected
              ? { order: '', direction: '' }
              : {
                  order: option.value.toString(),
                  direction: option.options.direction
                }
          );
        }}
        className={isSelected ? 'sort-option selected' : 'sort-option'}
        data-test="sort-option"
      >
        <input
          type="radio"
          checked={isSelected}
          style={{ accentColor: '#76ade1' }}
          readOnly
        />
        {option.options.displayedValue}
      </div>
    );
  };

  return (
    <Container isOpen={isOpen} data-test="sort-btn" ref={containerRef}>
      <button
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        className="sort-by-btn"
      >
        {isNawyNow ? t('sort') : t('sort_by')}
        {isNawyNow ? <NawyNowSortIcon /> : <SortIcon />}
      </button>

      {isOpen && (
        <div className="menu">
          <div className="submenu">
            {options.map((curr: InputOption, idx) => (
              <Input option={curr} key={idx} />
            ))}
          </div>
        </div>
      )}
    </Container>
  );
};

interface ContainerProps {
  isOpen: boolean;
}

const Container = styled.div<ContainerProps>`
  position: relative;
  height: 100%;
  .selected {
    color: ${props => props.theme.light.colors.white};
    background-color: ${props => props.theme.light.colors.primaryBlue};
    border-radius: 8px;
  }
  button.sort-by-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    font-family: materialRegular;
    font-size: 14px;
    background-color: transparent;
    color: #5b6573;
    border: 1px solid #c6d0de;
    border-radius: 12px;
    cursor: pointer;
    height: 100%;
    gap: 8px;
  }
  .menu {
    width: auto;
    position: absolute;
    background-color: ${props => props.theme.light.colors.white};
    color: ${props => props.theme.light.colors.black};
    top: 60px;
    right: 0px;
    font-family: materialRegular;
    font-size: ${props => props.theme.font.sizes.s};
    border-radius: 8px;
    border: 1px solid ${props => props.theme.light.borderColors.secondary};
    z-index: 20;
    .submenu {
      display: flex;
      flex-direction: column;
      justify-content: center;
      cursor: pointer;
      .sort-option {
        display: flex;
        align-items: center;
        white-space: nowrap;
        padding: 14px 24px;
        gap: 8px;
        &:not(.selected):hover {
          background-color: rgba(118, 173, 225, 0.08);
        }
        input {
          margin-right: 8px;
        }
      }
    }
  }
`;

export default SortByPresentation;
